/**
 * Error tracking and monitoring utility
 * Handles error logging, user feedback, and integration with error tracking services
 */

import { Logger } from './logger';

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export interface ErrorContext {
  userId?: string;
  userAgent?: string;
  url?: string;
  timestamp?: number;
  buildVersion?: string;
  environment?: string;
  sessionId?: string;
  additionalData?: Record<string, any>;
}

export interface TrackedError {
  message: string;
  stack?: string;
  name?: string;
  severity: ErrorSeverity;
  context: ErrorContext;
  fingerprint?: string;
}

/**
 * Error tracking service
 */
class ErrorTrackingService {
  private static instance: ErrorTrackingService;
  private isInitialized = false;
  private isDevelopment = import.meta.env.DEV;
  private buildVersion = import.meta.env.VITE_BUILD_VERSION || '1.0.0';
  private environment = import.meta.env.VITE_ENVIRONMENT || 'development';

  private constructor() {}

  public static getInstance(): ErrorTrackingService {
    if (!ErrorTrackingService.instance) {
      ErrorTrackingService.instance = new ErrorTrackingService();
    }
    return ErrorTrackingService.instance;
  }

  /**
   * Initialize error tracking
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // Set up global error handlers
    this.setupGlobalErrorHandlers();
    this.isInitialized = true;

    // Error tracking initialized
  }

  /**
   * Track an error
   */
  public trackError(
    error: Error | string,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    additionalContext: Partial<ErrorContext> = {}
  ): void {
    const context = this.buildContext(additionalContext);
    const trackedError = this.buildTrackedError(error, severity, context);

    // Log locally
    this.logError(trackedError);

    // In production, you would send to error tracking service
    if (!this.isDevelopment) {
      this.sendToErrorTracking(trackedError);
    }
  }

  /**
   * Track a user action that led to an error
   */
  public trackUserError(
    action: string,
    error: Error | string,
    userId?: string,
    additionalData?: Record<string, any>
  ): void {
    this.trackError(error, ErrorSeverity.MEDIUM, {
      userId,
      additionalData: {
        userAction: action,
        ...additionalData,
      },
    });
  }

  /**
   * Track authentication errors
   */
  public trackAuthError(error: Error | string, userId?: string, email?: string): void {
    this.trackError(error, ErrorSeverity.HIGH, {
      userId,
      additionalData: {
        category: 'authentication',
        email: email ? this.hashEmail(email) : undefined,
        timestamp: performance.now(), // Use performance.now() for timing/tracking
      },
    });
  }

  /**
   * Track API errors
   */
  public trackApiError(
    endpoint: string,
    method: string,
    statusCode: number,
    error: Error | string,
    requestId?: string
  ): void {
    const severity = statusCode >= 500 ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM;

    this.trackError(error, severity, {
      additionalData: {
        category: 'api',
        endpoint,
        method,
        statusCode,
        requestId,
        timestamp: performance.now(), // Use performance.now() for timing/tracking
      },
    });
  }

  /**
   * Track performance issues
   */
  public trackPerformanceIssue(
    operation: string,
    duration: number,
    threshold: number = 5000
  ): void {
    if (duration > threshold) {
      this.trackError(
        `Performance issue: ${operation} took ${duration}ms (threshold: ${threshold}ms)`,
        ErrorSeverity.LOW,
        {
          additionalData: {
            category: 'performance',
            operation,
            duration,
            threshold,
            timestamp: performance.now(), // Use performance.now() for timing/tracking
          },
        }
      );
    }
  }

  /**
   * Set user context for error tracking
   */
  public setUserContext(userId: string, email?: string, role?: string): void {
    // Store user context for inclusion in subsequent error reports
    if (typeof window !== 'undefined') {
      (window as any).__errorTrackingUserContext = {
        userId,
        email: email ? this.hashEmail(email) : undefined,
        role,
        timestamp: performance.now(), // Use performance.now() for timing/tracking
      };
    }
  }

  /**
   * Clear user context (e.g., on logout)
   */
  public clearUserContext(): void {
    if (typeof window !== 'undefined') {
      delete (window as any).__errorTrackingUserContext;
    }
  }

  /**
   * Build error context
   */
  private buildContext(additionalContext: Partial<ErrorContext> = {}): ErrorContext {
    const baseContext: ErrorContext = {
      timestamp: performance.now(), // Use performance.now() for timing/tracking
      buildVersion: this.buildVersion,
      environment: this.environment,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };

    // Include user context if available
    const userContext =
      typeof window !== 'undefined' ? (window as any).__errorTrackingUserContext : undefined;

    return {
      ...baseContext,
      ...userContext,
      ...additionalContext,
    };
  }

  /**
   * Build tracked error object
   */
  private buildTrackedError(
    error: Error | string,
    severity: ErrorSeverity,
    context: ErrorContext
  ): TrackedError {
    if (typeof error === 'string') {
      return {
        message: error,
        severity,
        context,
        fingerprint: this.generateFingerprint(error, context),
      };
    }

    return {
      message: error.message,
      stack: error.stack,
      name: error.name,
      severity,
      context,
      fingerprint: this.generateFingerprint(error.message, context),
    };
  }

  /**
   * Log error locally
   */
  private logError(trackedError: TrackedError): void {
    const logLevel = this.getSeverityLogLevel(trackedError.severity);

    const errorData = {
      message: trackedError.message,
      name: trackedError.name ?? 'Unknown Error',
      fingerprint: trackedError.fingerprint,
    };

    Logger[logLevel]('Error tracked', {
      error: errorData,
      severity: trackedError.severity,
      context: trackedError.context,
      stack: trackedError.stack,
    });
  }

  /**
   * Set up global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', event => {
      this.trackError(event.error || event.message, ErrorSeverity.HIGH, {
        additionalData: {
          category: 'javascript',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    window.addEventListener('unhandledrejection', event => {
      this.trackError(event.reason, ErrorSeverity.HIGH, {
        additionalData: {
          category: 'unhandled_promise_rejection',
        },
      });
    });
  }

  /**
   * Generate error fingerprint for grouping
   */
  private generateFingerprint(message: string, context: ErrorContext): string {
    const key = `${message}_${context.url}_${this.buildVersion}`;
    return btoa(key).substring(0, 12);
  }

  /**
   * Hash email for privacy
   */
  private hashEmail(email: string): string {
    return btoa(email).substring(0, 8);
  }

  /**
   * Get log level based on severity
   */
  private getSeverityLogLevel(severity: ErrorSeverity): 'info' | 'warn' | 'error' {
    switch (severity) {
      case ErrorSeverity.LOW:
        return 'info';
      case ErrorSeverity.MEDIUM:
        return 'warn';
      case ErrorSeverity.HIGH:
      case ErrorSeverity.CRITICAL:
        return 'error';
      default:
        return 'warn';
    }
  }

  /**
   * Send error to tracking service (placeholder for production implementation)
   */
  private sendToErrorTracking(_: TrackedError): void {
    // Placeholder for production error tracking service integration
    Logger.warn('Error tracking service not configured for production', {
      context: 'ErrorTrackingService',
      operation: 'sendToErrorTracking',
    });
  }
}

// Export singleton instance
export const ErrorTracker = ErrorTrackingService.getInstance();

// Export convenience functions
